import axios from 'axios';
import { useCallback, useState } from 'react';
import { apiUrl } from '../../../../dataProvider';
import { IMAGE_LAYER_TYPE } from '../constants';

const formatSignetMetadata = (dataset = {}) => {
  return {
    ring_material: dataset.material ? JSON.parse(dataset.material)?.sku || '' : '',
    shape: dataset.shape || '',
    size: dataset.size || '',
    shank: dataset.shank || '',
    school: dataset.school || '',
    engraving: dataset.engraving || '',
    cerakote_color: dataset.color || '',
    image_number: 1,
    layer_type: dataset.layer || '',
    inlay_material: dataset.inlay ? JSON.parse(dataset.inlay)?.sku || '' : '',
    ring_type: IMAGE_LAYER_TYPE.SIGNET,
  };
};

const validateSvgElement = (element) => {
  if (!element || !(element instanceof SVGElement)) {
    throw new Error('Invalid SVG element');
  }
  if (!element.dataset.material || !element.dataset.profile || !element.dataset.width) {
    throw new Error('SVG element missing required dataset properties');
  }
  return true;
};

const commonBandMetadata = (dataset = {}, imageNumber = 1) => {
  if (!dataset.material || !dataset.profile || !dataset.width) {
    throw new Error('Missing required metadata fields');
  }
  return {
    ring_material: dataset.material ? JSON.parse(dataset.material)?.sku || '' : '',
    profile: dataset.profile || '',
    width: dataset.width || '',
    engraving: dataset.engraving || '',
    image_number: imageNumber,
    ring_type: IMAGE_LAYER_TYPE.BAND,
    processed_at: new Date().toISOString(),
  };
};

const formatDesignPatternMetadata = (dataset = {}, imageNumber = 1) => ({
  ...commonBandMetadata(dataset, imageNumber),
  cerakote_color: dataset.color || '',
  layer_type: dataset.layer || '',
  image_type: 'main',
});

const formatInsideBandMetadata = (dataset = {}, imageNumber = 1) => ({
  ...commonBandMetadata(dataset, imageNumber),
  layer_type: IMAGE_LAYER_TYPE.INSIDEBAND,
  image_type: 'main',
});

const formatCerakoteInsideBandMetadata = (dataset = {}, imageNumber = 1) => ({
  ...commonBandMetadata(dataset, imageNumber),
  cerakote_color: dataset.color || '',
  layer_type: dataset.layer || '',
  image_type: 'engraving',
});

export const useImageProcessing = () => {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ 
    current: 0, 
    total: 0, 
    message: '', 
    stage: '',
    mainImages: 0,
    engravingImages: 0,
    processedMain: 0,
    processedEngraving: 0
  });

  const convertSvgToPng = (svgElement, width = 1200, height = 1200) => {
    return new Promise((resolve, reject) => {
      try {
        // Wait for next frame to ensure SVG is rendered
        requestAnimationFrame(() => {
          try {
            // Clone the SVG to avoid modifying the original
            const clonedSvg = svgElement.cloneNode(true);

            // Set dimensions explicitly
            clonedSvg.setAttribute('width', width);
            clonedSvg.setAttribute('height', height);
            clonedSvg.setAttribute('viewBox', '0 0 1200 1200');

            // Ensure all elements are rendered
            const allElements = clonedSvg.getElementsByTagName('*');
            for (const element of allElements) {
              if (element.getBBox) {
                try {
                  const bbox = element.getBBox();
                  // Add a small offset to ensure element is rendered
                  element.setAttribute('data-bbox', `${bbox.x},${bbox.y},${bbox.width},${bbox.height}`);
                } catch (e) {
                  console.warn('Could not get bbox for element:', element);
                }
              }
            }

            const svgString = new XMLSerializer().serializeToString(clonedSvg);
            const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);

            const img = new Image();
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            img.onload = () => {
              ctx.clearRect(0, 0, width, height);
              ctx.drawImage(img, 0, 0, width, height);
              canvas.toBlob(
                (blob) => {
                  URL.revokeObjectURL(url);
                  resolve(blob);
                },
                'image/png',
                1.0,
              );
            };

            img.onerror = (error) => {
              URL.revokeObjectURL(url);
              reject(error);
            };

            img.src = url;
          } catch (innerError) {
            reject(innerError);
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  };

  // Upload files in batches of 5 per request
  const uploadPngBatches = async (files, uploadUrl, batchSize = 5, onProgress) => {
    const results = [];
    const errors = [];
    const totalBatches = Math.ceil(files.length / batchSize);
    const maxRetries = 0;
    const retryDelay = 1000;

    const uploadBatchWithRetry = async (batchFiles, retryCount = 0) => {
      try {
        const formData = new FormData();

        batchFiles.forEach((file, index) => {
          formData.append(`files[${index}]`, file.blob);

          // Add metadata fields for each file
          Object.entries(file.metadata).forEach(([key, value]) => {
            formData.append(`metadata[${index}][${key}]`, value);
          });
        });

        // Fix: Use axios response handling correctly
        const response = await axios.post(uploadUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Fix: axios already returns parsed data
        return response.data;
      } catch (error) {
        if (retryCount < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return uploadBatchWithRetry(batchFiles, retryCount + 1);
        }
        throw error;
      }
    };

    for (let i = 0; i < files.length; i += batchSize) {
      const batch = files.slice(i, i + batchSize);

      try {
        const batchResult = await uploadBatchWithRetry(batch);
        results.push(batchResult);
      } catch (error) {
        console.error(`Failed to upload batch starting at index ${i}:`, error);
        batch.forEach((file) => {
          errors.push({ id: file.id, error });
        });
      }

      onProgress?.(Math.floor(i / batchSize) + 1, totalBatches);

      // Add delay between batches if not the last batch
      if (i + batchSize < files.length) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    return {
      successful: results,
      failed: errors,
    };
  };

  const processVariations = useCallback(async (imageLayerType) => {
    console.log('Starting processVariations...');
    setProcessing(true);
    const results = [];
    const batchSize = 10;
    let mainImagesCount = 0;
    let engravingImagesCount = 0;

    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const svgElements = document.querySelectorAll('.variant-svg');
      const engravingSvgElements = document.querySelectorAll('.engraving-svg');
      const updatedEngravingSvgElements = [];
      
      console.log('Found SVG elements:', {
        main: svgElements.length,
        engraving: engravingSvgElements.length
      });

      if (svgElements.length === 0 && engravingSvgElements.length === 0) {
        throw new Error('No SVG elements found');
      }

      svgElements.forEach(element => {
        const engravingSvgElement = element.querySelector('.engraving-svg');
        if(!engravingSvgElement) return;
        
        // Create a new SVG element and copy all attributes from the original
        const parentSvgEngraving = element.cloneNode(true)
        
        // Set specific data attributes
        parentSvgEngraving.setAttribute('data-layer', 'cerakote_inside_pattern');

        const ringBackground = parentSvgEngraving.querySelector('.ring-background');
        if(ringBackground){
          parentSvgEngraving.removeChild(ringBackground);
        }
        
        // Debug logging to help identify validation issues
        console.log('Created engraving SVG:', {
          element: parentSvgEngraving,
          innerHTML: parentSvgEngraving.innerHTML,
          attributes: Array.from(parentSvgEngraving.attributes).map(attr => `${attr.name}="${attr.value}"`),
          isValid: validateSvgElement(parentSvgEngraving)
        });
        
        if(validateSvgElement(parentSvgEngraving)){
          updatedEngravingSvgElements.push(parentSvgEngraving);
        } else {
          console.warn('Invalid engraving SVG element:', parentSvgEngraving);
        }
      });

      // Convert NodeList to Array for easier batch processing
      const svgArray = Array.from(svgElements);
      const engravingSvgArray = Array.from(updatedEngravingSvgElements);
      
      mainImagesCount = svgArray.length;
      engravingImagesCount = engravingSvgArray.length;

      if (engravingSvgArray.length > 0) {
        svgArray.push(...engravingSvgArray);
      }

      const totalBatches = Math.ceil(svgArray.length / batchSize);

      setProgress({
        current: 0,
        total: totalBatches,
        message: 'Starting conversion...',
        stage: 'converting',
        mainImages: mainImagesCount,
        engravingImages: engravingImagesCount,
      });

      // Process SVGs in batches
      for (let i = 0; i < svgArray.length; i += batchSize) {
        const batch = svgArray.slice(i, i + batchSize);
        
        // Process batch in parallel
        const batchPromises = batch.map(async (svgElement, batchIndex) => {
          console.log('Processing SVG element:', {
            element: svgElement,
            isEngraving: svgElement.classList.contains('engraving-svg')
          });

          try {
            // Convert to PNG
            const pngBlob = await convertSvgToPng(svgElement);
            const previewUrl = URL.createObjectURL(pngBlob);

            // Determine if this is an engraving element
            const isEngraving = svgElement.classList.contains('engraving-svg');
            
            let metadata = {};
            try {
              switch (imageLayerType) {
                case IMAGE_LAYER_TYPE.BAND:
                  metadata = formatDesignPatternMetadata(svgElement.dataset, 1);
                  break;
                case IMAGE_LAYER_TYPE.INSIDEBAND:
                  if (svgElement.dataset.layer === 'cerakote_inside_pattern') {
                    metadata = formatCerakoteInsideBandMetadata(svgElement.dataset, 1);
                  } else {
                    metadata = formatInsideBandMetadata(svgElement.dataset, 1);
                  }
                  break;
                default:
                  metadata = formatSignetMetadata(svgElement.dataset);
                  break;
              }
            } catch (error) {
              console.error('Error formatting metadata:', error);
              throw new Error(`Metadata formatting failed: ${error.message}`);
            }

            return {
              id: `variation_${i + batchIndex}_${isEngraving ? 'engraving' : 'main'}`,
              pngBlob,
              previewUrl,
              metadata,
              shape: imageLayerType === IMAGE_LAYER_TYPE.SIGNET ? metadata.shape : metadata.profile,
              isEngraving
            };
          } catch (error) {
            console.error('Error processing SVG:', error);
            return null;
          }
        });

        // Wait for all SVGs in the batch to be processed
        const batchResults = await Promise.all(batchPromises);
        const validResults = batchResults.filter(Boolean);
        
      
        results.push(...validResults);

        // Update progress with detailed counts
        setProgress(prev => ({
          current: Math.floor(i / batchSize) + 1,
          total: totalBatches,
          message: `Converting batch ${Math.floor(i / batchSize) + 1} of ${totalBatches}`,
          stage: 'converting',
          mainImages: mainImagesCount,
          engravingImages: engravingImagesCount,
        }));

        // Add a small delay between batches to prevent browser from freezing
        if (i + batchSize < svgArray.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Upload the processed images
      if (results.length > 0) {
        setProgress(prev => ({
          ...prev,
          current: 0,
          total: Math.ceil(results.length / 5),
          message: 'Starting upload...',
          stage: 'uploading'
        }));

        const uploadResults = await uploadPngBatches(
          results.map((result, index) => ({
            id: result.id,
            blob: result.pngBlob,
            metadata: result.metadata,
          })),
          `${apiUrl}/upload-files`,
          5,
          (current, total) => {
            setProgress(prev => ({
              ...prev,
              current,
              total,
              message: `Uploading batch ${current} of ${total}`,
              stage: 'uploading'
            }));
          },
        );

        console.log('Upload results:', {
          total: results.length,
          main: results.filter(r => !r.isEngraving).length,
          engraving: results.filter(r => r.isEngraving).length,
          uploadResults
        });
      }

      return results;
    } catch (error) {
      console.error('Error processing variations:', error);
      throw error;
    } finally {
      setProcessing(false);
    }
  }, []);

  return {
    processing,
    progress,
    processVariations,
  };
};
